import { ReactNode } from 'react';

import { c } from 'ttag';

import {
    Badge,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCellProps,
    TableRow,
} from '@freelancelabs/components';
import { EyeOutline } from '@freelancelabs/icons';
import { SORT_DIRECTION } from '@freelancelabs/shared';

import { WRAPPING_HEADER } from '../../components/ListCard';
import { SortDirection } from '../../graphql/__generated__/schema';
import { AccountsQuery } from '../../graphql/__generated__/types';

export type AccountRow = NonNullable<NonNullable<AccountsQuery['accounts']>['items']>[number];

/**
 * Le tableau des comptes, partagé par les trois rendus de la page (PCG-333) : le
 * dépliage d'une société, la liste des indépendants, et la vue à plat. Ils rendent
 * les mêmes cellules et les mêmes règles de compteur ; les écrire trois fois donnerait
 * trois idées de ce qu'est un SIREN nul.
 *
 * La vue à plat seule ajoute la colonne « Société mère » (`showCompany`) et rend son
 * nom triable (`onSortDirection`) ; les deux autres trient par nom en ascendant côté
 * serveur, sans en-tête cliquable.
 */
interface Props {
    rows: AccountRow[];
    /** Dénominateur du « X / 6 », lu sur `portalModules`, jamais écrit en dur. */
    moduleTotal: number | null;
    /** La vue à plat seule montre la société mère en colonne. */
    showCompany?: boolean;
    /** Comptes d'une société (vue groupée) : chaque ligne porte une petite branche. */
    nested?: boolean;
    /** Sens du tri du nom, quand la colonne est triable (vue à plat). */
    sortDirection?: SortDirection;
    onSortDirection?: (direction: SortDirection) => void;
    loading: boolean;
    hasError: boolean;
    /** L'état d'erreur ou de vide, écrit par l'appelant : il nomme le cas. */
    state?: ReactNode;
}

/**
 * La cellule société de la vue à plat. Trois cas et non un, deux d'entre eux se
 * rendraient sinon en un nom nu qui dit une chose fausse. 92 % des comptes du parc
 * sont leur propre racine, donc réimprimer le nom le répéterait sur presque chaque
 * ligne ; une racine nulle SIGNALE une boucle et non « pas de société ».
 */
const companyCell = (account: AccountRow) => {
    if (account.rootOrganizationId && account.rootOrganizationId === account.id) {
        return <span className="text-text-500">{c('Accounts list').t`This account`}</span>;
    }
    if (!account.rootOrganizationName) {
        return <span className="text-text-500">{c('Accounts list').t`Company not resolved`}</span>;
    }
    return account.rootOrganizationName;
};

/** Un compteur que l'API n'a pas envoyé. Un blanc dans une colonne de chiffres se lit zéro. */
const countCell = (value: number | null | undefined) =>
    value === null || value === undefined ? (
        <span className="font-sans text-text-500">{c('Accounts list').t`Not counted`}</span>
    ) : (
        value
    );

/** Zéro utilisateur s'écrit « — » : une colonne de chiffres à zéro se lit mieux vide de sens. */
const userCell = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
        return countCell(value);
    }
    return value === 0 ? <span className="text-text-500">—</span> : value;
};

/** Le SIREN en chip. Absent, un chip en pointillé « SIREN — » plutot qu'un tiret nu. */
const formatSiren = (siren: string) => siren.replace(/^(\d{3})(\d{3})(\d{3})$/, '$1 $2 $3');
const sirenCell = (siren: string | null | undefined) =>
    siren ? (
        <span className="inline-block rounded border border-[color:var(--portal-fiche-border)] px-1.5 py-0.5 font-mono text-xs text-text-700">
            {formatSiren(siren)}
        </span>
    ) : (
        <span className="inline-block rounded border border-dashed border-[color:var(--portal-fiche-border)] px-1.5 py-0.5 font-mono text-xs text-text-400">
            {c('Accounts list').t`SIREN —`}
        </span>
    );

const AccountsTable = ({
    rows,
    moduleTotal,
    showCompany = false,
    nested = false,
    sortDirection,
    onSortDirection,
    loading,
    hasError,
    state,
}: Props) => {
    // La largeur des cellules du design system coupe à l'ellipse ; un compte long est
    // illisible coupé au tiers. `!` car styles est importé après Tailwind.
    const wrapping = '!whitespace-normal !break-words py-2';

    const columns: TableHeaderCellProps[] = (
        [
            {
                id: 'name',
                label: c('Accounts column').t`Account`,
                minWidth: 260,
                ...(onSortDirection && {
                    direction: sortDirection === SortDirection.Asc ? SORT_DIRECTION.ASC : SORT_DIRECTION.DSC,
                    onSort: (next: SORT_DIRECTION) =>
                        onSortDirection(next === SORT_DIRECTION.ASC ? SortDirection.Asc : SortDirection.Desc),
                }),
            },
            ...(showCompany
                ? [
                      {
                          id: 'company',
                          label: c('Accounts column').t`Head office`,
                          minWidth: 220,
                          textHelp: c('Accounts column')
                              .t`The client the account belongs to, at the top of its tree. It is shown to be read, not to be opened: it may sit outside your portfolio. An account with no parent is the top of its own tree, and says so.`,
                      },
                  ]
                : []),
            {
                id: 'siren',
                label: c('Accounts column').t`SIREN`,
                minWidth: 120,
                textHelp: c('Accounts column')
                    .t`The account's own SIREN, as ingested. It is filled on about four accounts in five, so a dash is the ordinary case and not a fault.`,
            },
            { id: 'portal', label: c('Accounts column').t`Portal access`, minWidth: 130 },
            {
                id: 'users',
                label: c('Accounts column').t`Users`,
                minWidth: 90,
                textAlign: 'end',
                textHelp: c('Accounts column')
                    .t`People holding a live access on this account, invited ones included. Revoked and departed accesses are not counted: they are the history of an access, not an access.`,
            },
            {
                id: 'modules',
                label: c('Accounts column').t`Modules`,
                minWidth: 100,
                textAlign: 'end',
                textHelp: c('Accounts column')
                    .t`Modules open on this account, out of the number of entries the portal menu names. That second number is read on the module referential, never written here, and it grows with the menu.`,
            },
            { id: 'actions', label: '', minWidth: 130, textAlign: 'end' },
        ] as TableHeaderCellProps[]
    ).map((column) => ({ ...column, className: WRAPPING_HEADER }));

    return (
        <>
            <Table className="text-[13px]">
                <TableHeader resizable cells={columns} />
                <TableBody loading={loading} loadingRows={5} loadingColumns={columns.length}>
                    {(hasError ? [] : rows).map((account) => (
                        <TableRow key={account.id}>
                            <TableCell className={wrapping}>
                                <span className="flex items-center gap-2">
                                    {/* La branche ne se rend que sur un compte enfant (vue groupee). */}
                                    {nested && (
                                        <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            aria-hidden="true"
                                            className="shrink-0 text-text-300"
                                        >
                                            <path d="M6 3v10a4 4 0 0 0 4 4h8" />
                                        </svg>
                                    )}
                                    <span className="font-semibold">{account.name}</span>
                                </span>
                            </TableCell>
                            {showCompany && <TableCell className={wrapping}>{companyCell(account)}</TableCell>}
                            <TableCell>{sirenCell(account.siren)}</TableCell>
                            <TableCell>
                                {/* Nul n'est pas « fermé » : un champ absent rendu fermé
                                    annoncerait un état que personne n'a posé. */}
                                {account.portalEnabled === null || account.portalEnabled === undefined ? (
                                    <span className="text-sm text-text-500">{c('Accounts list').t`Not stated`}</span>
                                ) : (
                                    <Badge category={account.portalEnabled ? 'success' : 'default'} size="small">
                                        {account.portalEnabled
                                            ? c('Portal access').t`• Open`
                                            : c('Portal access').t`• Closed`}
                                    </Badge>
                                )}
                            </TableCell>
                            <TableCell className="text-end font-mono text-xs">{userCell(account.userCount)}</TableCell>
                            <TableCell className="text-end font-mono text-xs">
                                {countCell(account.moduleCount)}
                                {/* Le dénominateur seulement une fois le référentiel répondu.
                                    « 2 / 0 » en vol serait une fraction fausse ; le
                                    numérateur seul reste vrai. */}
                                {moduleTotal !== null &&
                                    account.moduleCount !== null &&
                                    account.moduleCount !== undefined && (
                                        <span className="text-text-500"> / {moduleTotal}</span>
                                    )}
                            </TableCell>
                            <TableCell className="text-end">
                                <Button
                                    to={`/comptes/${account.id}`}
                                    category="weak"
                                    size="small"
                                    icon
                                    aria-label={c('Accounts').t`View the account`}
                                    title={c('Accounts').t`View the account`}
                                >
                                    <EyeOutline size={16} />
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {state}
        </>
    );
};

export default AccountsTable;
